<template>
  <modal-dialog
    :visible="dialog"
    persistent
    scrollable
    max-width="500px"
    :title="$t('title.edit_duration_rule')"
  >
    <!-- Confirm Deletion -->
    <confirmation-dialog
      :visible="deleteConfirmation"
      :message="$t('message.confirm_delete_rule')"
      @confirm="remove"
      @close="deleteConfirmation = false"
    />

    <validation-observer ref="observer">
      <v-form ref="form">
        <!-- Party Size -->
        <section-header :title="$t('label.party_size')" />
        <v-row>
          <v-col
            cols="6"
            class="pt-3 pb-0"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpFrom"
              rules="required|lteq:@vpTo"
            >
              <v-select
                v-model="rule.partySizeFrom"
                :label="$t('label.from')"
                :error-messages="errors"
                :items="guestsItems"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="6"
            class="pt-3 pb-0"
          >
            <validation-provider
              v-slot="{ errors }"
              name="vpTo"
              rules="required|gteq:@vpFrom"
            >
              <v-select
                v-model="rule.partySizeTo"
                :label="$t('label.to')"
                :error-messages="errors"
                :items="guestsItems"
              />
            </validation-provider>
          </v-col>

          <v-col
            cols="12"
            class="py-0"
          >
            <section-header
              :title="$t('label.time')"
              class="py-0"
            />
          </v-col>

          <!-- Time -->
          <template v-if="!allDay">
            <v-col
              cols="6"
              class="pt-2 pb-3"
            >
              <validation-provider
                v-slot="{ errors }"
                name="vpTimeFrom"
                rules="required|lteqTime:@vpTimeTo"
              >
                <time-picker-field
                  v-model="rule.timeFrom"
                  :error-messages="errors"
                  :label="$t('label.from')"
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="6"
              class="pt-2 pb-3"
            >
              <validation-provider
                v-slot="{ errors }"
                name="vpTimeTo"
                rules="required|gteqTime:@vpTimeFrom"
              >
                <time-picker-field
                  v-model="rule.timeTo"
                  :error-messages="errors"
                  :label="$t('label.to_including')"
                />
              </validation-provider>
            </v-col>
          </template>

          <v-col
            cols="12"
            class="py-0"
          >
            <v-switch
              :input-value="allDay"
              :label="$t('label.all_day')"
              class="py-0"
              @change="changeAllDay"
            />
          </v-col>
          <v-col
            cols="6"
            class="py-0"
          />

          <!-- Additional Time -->
          <v-col
            cols="12"
            class="py-0"
          >
            <section-header :title="$t('label.additional_time')" />
          </v-col>

          <v-col
            cols="6"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="rule.additionalDuration"
                :label="$t('label.time')"
                :items="additionalTimeItems"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>
      </v-form>
    </validation-observer>

    <!-- Actions -->
    <template #actions>
      <v-btn
        color="error"
        :disabled="isNew"
        @click="deleteConfirmation = true"
      >
        {{ $t('button.delete_rule') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import moment from 'moment';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import { performAction, performSaveAction } from '@/services/vue-utils';
import DurationRule from '@/model/DurationRule';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import DialogClosable from '@/mixins/dialog-closable';
import { ValidationObserver } from 'vee-validate';

export default Vue.extend({
  name: 'EditDurationRuleDialog',
  components: { ConfirmationDialog },
  mixins: [DialogClosable],
  props: {
    durationRule: { type: Object as PropType<DurationRule>, required: true },
  },
  data() {
    return {
      deleteConfirmation: false,
      rule: new DurationRule(),
      oldRule: new DurationRule(),
      guestsItems: [...new Array(99)].map((e, i) => ({ value: i + 1, text: this.$i18n.t('label.x_guests', [i + 1]) })),
      additionalTimeItems: [...new Array(7)].map((e, i) => {
        const minutes = i === 0 ? i - 1 * 15 : i * 15;
        const text = this.$localized.durationFromSeconds(minutes * 60);
        return { text, value: minutes };
      }),

    };
  },
  computed: {
    allDay(): boolean {
      const date = moment(this.rule.timeFrom ?? '', 'HH:mm');
      return !date.isValid();
    },
    isNew(): boolean {
      return !this.rule.id;
    },
    isChanged(): boolean {
      return !isModelEqualToModel(this.rule, this.oldRule);
    },

  },
  watch: {
    visible() {
      this.reset();
    },
  },
  mounted() {

  },
  methods: {
    reset() {
      this.rule = cloneModel(this.durationRule);
      this.oldRule = cloneModel(this.durationRule);
    },
    close() {
      (this.$refs.observer as InstanceType<typeof ValidationObserver>).reset();
      this.$emit('close');
    },
    async save() {
      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch('sendDurationRule', { entity: this.rule }),
      );

      if (ok) {
        this.close();
      }
    },
    cancel() {
      this.close();
    },
    async remove() {
      const ok = await performAction(
        this.$tc('message.success_rule_deleted'),
        this.$tc('error.deleting_rule'),
        async () => this.$tstore.dispatch('sendDurationRule', { entity: this.rule, delete: true }),
      );
      if (ok) {
        this.close();
      }
    },
    changeAllDay(toAllDay: boolean) {
      this.rule.timeFrom = toAllDay ? '' : '10:00';
      this.rule.timeTo = toAllDay ? '' : '14:00';
    },
  },
});
</script>
